import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import SearchIcon from "@mui/icons-material/Search";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TuneIcon from "@mui/icons-material/Tune";
import { useHistory } from "react-router-dom";
import { PageTopHelpButton } from "src/components/help";
import {
  useListParts,
  useSearchParts,
  useGetPartFacets,
  type PartFacetsParams,
  type PartsSearchParams,
  type PartListItem,
} from "../services/PartService";
import {
  useListPartSchemas,
  useGetEffectiveFeatures,
} from "../services/PartSchemaService";
import { useListPartGroups } from "../services/PartGroupService";
import { useDebounce } from "src/reusable/hooks/useDebounce";
import { useFilterUrlSync } from "src/hooks/useFilterUrlSync";
import { usePageUrlSync } from "src/hooks/usePageUrlSync";
import FacetedFilterSidebar from "src/components/filters/FacetedFilterSidebar";
import AppliedFiltersBar from "src/components/filters/AppliedFiltersBar";
import {
  buildPartsFilterConfig,
  partFacetsToFilterOptions,
  applyNumericRangeBounds,
  appliedFiltersToSearchBody,
} from "../config/partsFilterConfig";
import PartCardList from "./PartCardList";
import CreatePartModal from "../components/CreatePartModal";
import PartExcelUploadModal from "../components/PartExcelUploadModal";
import PartGroupCreateModal from "../components/PartGroupCreateModal";

const PAGE_SIZE = 50;

interface MenuAction {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  testId?: string;
}

/**
 * A single header "super-button" that opens a menu of related actions instead
 * of taking up one slot per action.
 */
const ActionMenuButton = ({
  label,
  icon,
  variant,
  actions,
  testId,
}: {
  label: string;
  icon: ReactNode;
  variant: "contained" | "outlined";
  actions: MenuAction[];
  testId?: string;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  // Unique per instance (this component renders more than once per page), so
  // the button/menu aria wiring below never produces duplicate DOM ids.
  const buttonId = useId();
  const menuId = useId();

  return (
    <>
      <Button
        id={buttonId}
        variant={variant}
        startIcon={icon}
        endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        data-testid={testId}
      >
        {label}
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ "aria-labelledby": buttonId }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.label}
            data-testid={action.testId}
            onClick={() => {
              setAnchorEl(null);
              action.onClick();
            }}
          >
            <ListItemIcon>{action.icon}</ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const PartsDashboardPage = () => {
  const history = useHistory();

  // Free-text + group filters (apply in both list and schema-search modes).
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [selectedGroupId, setSelectedGroupId] = useState<number | "">("");

  // Selected part type / schema drives the structured feature filtering.
  const [selectedSchemaId, setSelectedSchemaId] = useState<number | "">("");

  const { pagination, setPagination, resetPage } = usePageUrlSync({
    pageSize: PAGE_SIZE,
  });
  const page = pagination.pageIndex + 1;
  const setPage = (next: number) =>
    setPagination((prev) => ({ ...prev, pageIndex: next - 1 }));

  // Modals
  const [createPartOpen, setCreatePartOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);

  const { data: partGroups } = useListPartGroups();
  const { data: partSchemas } = useListPartSchemas();

  const schemaMode = selectedSchemaId !== "";
  const schemaId = schemaMode ? (selectedSchemaId as number) : undefined;

  // Schema-driven filter config (field names stable per schema).
  const { data: effectiveFeatures, isLoading: isFeaturesLoading } =
    useGetEffectiveFeatures(schemaId);
  const baseFilterConfig = useMemo(
    () => buildPartsFilterConfig(effectiveFeatures),
    [effectiveFeatures],
  );
  const filterableFields = useMemo(
    () => baseFilterConfig.map((c) => c.fieldName),
    [baseFilterConfig],
  );

  const {
    appliedFilters,
    toggleFilterValue,
    setRangeFilter,
    setTextFilter,
    removeFilter,
    removeFiltersByField,
    clearFilters,
    hasActiveFilters,
    userFilterVersion,
  } = useFilterUrlSync({
    filterConfig: baseFilterConfig,
    filterableFields,
  });

  // Convert the current selection into the structured search/facets body.
  const featureBody = useMemo(
    () => appliedFiltersToSearchBody(appliedFilters, baseFilterConfig),
    [appliedFilters, baseFilterConfig],
  );

  // Dynamic facet counts/ranges over the current filtered set.
  const facetsParams: PartFacetsParams | null = useMemo(() => {
    if (!schemaId) return null;
    return {
      part_schema_fk: schemaId,
      part_group_fk: selectedGroupId === "" ? undefined : selectedGroupId,
      search: debouncedSearch || undefined,
      ...featureBody,
    };
  }, [schemaId, selectedGroupId, debouncedSearch, featureBody]);

  const { data: facetsData, isLoading: isFacetsLoading } =
    useGetPartFacets(facetsParams);

  const facetOptions = useMemo(
    () => partFacetsToFilterOptions(facetsData),
    [facetsData],
  );
  const sidebarConfig = useMemo(
    () => applyNumericRangeBounds(baseFilterConfig, facetsData),
    [baseFilterConfig, facetsData],
  );

  // Structured search (schema mode).
  const searchParams: PartsSearchParams = useMemo(
    () => ({
      part_schema_fk: schemaId,
      part_group_fk: selectedGroupId === "" ? undefined : selectedGroupId,
      search: debouncedSearch || undefined,
      ...featureBody,
      page,
      page_size: PAGE_SIZE,
    }),
    [schemaId, selectedGroupId, debouncedSearch, featureBody, page],
  );

  const { data: searchData, isLoading: isSearchLoading } = useSearchParts(
    searchParams,
    { enabled: schemaMode },
  );

  // Plain list (no part type selected) — existing behavior.
  const { data: listData, isLoading: isListLoading } = useListParts({
    search: debouncedSearch || undefined,
    part_group_fk: selectedGroupId === "" ? undefined : selectedGroupId,
    page,
    page_size: PAGE_SIZE,
  });

  const partsData = schemaMode ? searchData : listData;
  const isLoading = schemaMode
    ? isSearchLoading || isFeaturesLoading
    : isListLoading;

  // Reset to the first page on any user-initiated filter/search/scope change.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    resetPage();
  }, [
    userFilterVersion,
    debouncedSearch,
    selectedGroupId,
    selectedSchemaId,
    resetPage,
  ]);

  // Clear feature filters when switching part type — the canonical keys differ.
  const handleSchemaChange = (raw: string) => {
    setSelectedSchemaId(raw === "" ? "" : Number(raw));
    clearFilters();
  };

  const handlePartClick = (part: PartListItem) => {
    history.push(`/parts/detail/${part.id}`);
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        maxWidth: 1400,
        mx: "auto",
        p: 3,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mb: 3,
          flexWrap: "wrap",
          rowGap: 1.5,
          columnGap: 2,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Parts Catalog
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexShrink: 0, flexWrap: "wrap", rowGap: 1 }}
        >
          <PageTopHelpButton productLabel="Parts AI" product="parts-ai" />
          <Button
            variant="outlined"
            startIcon={<TuneIcon />}
            onClick={() => history.push("/parts/schemas")}
            data-testid="parts-manage-schemas-btn"
          >
            Schemas
          </Button>
          <Button
            variant="outlined"
            startIcon={<GroupWorkIcon />}
            onClick={() => setCreateGroupOpen(true)}
          >
            New Group
          </Button>
          <ActionMenuButton
            variant="outlined"
            label="Add Parts"
            icon={<AddIcon />}
            testId="parts-add-parts-menu-btn"
            actions={[
              {
                label: "New Part",
                icon: <AddIcon fontSize="small" />,
                onClick: () => setCreatePartOpen(true),
                testId: "parts-new-part-btn",
              },
              {
                label: "Upload Parts",
                icon: <UploadFileIcon fontSize="small" />,
                onClick: () => setUploadOpen(true),
                testId: "parts-upload-parts-btn",
              },
            ]}
          />
          <ActionMenuButton
            variant="contained"
            label="Chat"
            icon={<AddCircleIcon />}
            testId="parts-create-menu-btn"
            actions={[
              {
                label: "New Part Chat",
                icon: <Inventory2OutlinedIcon fontSize="small" />,
                onClick: () => history.push("/parts/chat"),
                testId: "parts-new-part-chat-btn",
              },
              {
                label: "New Search",
                icon: <SearchIcon fontSize="small" />,
                onClick: () => history.push("/parts/search"),
                testId: "parts-new-search-btn",
              },
              {
                label: "New Comparison",
                icon: <CompareArrowsIcon fontSize="small" />,
                onClick: () => history.push("/parts/compare"),
                testId: "parts-new-comparison-btn",
              },
            ]}
          />
        </Stack>
      </Stack>

      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2, flexShrink: 0, flexWrap: "wrap", rowGap: 1.5 }}
      >
        <TextField
          size="small"
          placeholder="Search parts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Part Group</InputLabel>
          <Select
            value={selectedGroupId}
            label="Part Group"
            onChange={(e) => {
              const raw = e.target.value;
              setSelectedGroupId(raw === "" ? "" : Number(raw));
            }}
          >
            <MenuItem value="">All Groups</MenuItem>
            {partGroups?.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name} ({g.part_count})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Part Type</InputLabel>
          <Select
            value={selectedSchemaId}
            label="Part Type"
            onChange={(e) => handleSchemaChange(String(e.target.value))}
            data-testid="parts-schema-select"
          >
            <MenuItem value="">All Types</MenuItem>
            {partSchemas?.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.part_type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Applied filters chips (schema mode) */}
      {schemaMode && hasActiveFilters && (
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <AppliedFiltersBar
            filters={appliedFilters}
            onRemove={removeFilter}
            onClearAll={clearFilters}
          />
        </Box>
      )}

      {/* Body: optional filter sidebar + card list */}
      <Stack direction="row" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
        {schemaMode && baseFilterConfig.length > 0 && (
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
              alignSelf: "flex-start",
              maxHeight: "100%",
            }}
          >
            <FacetedFilterSidebar
              filterConfig={sidebarConfig}
              appliedFilters={appliedFilters}
              facetData={facetOptions}
              onToggleValue={toggleFilterValue}
              onSetRange={setRangeFilter}
              onSetDateRange={() => {}}
              onSetText={(fieldName, value) => setTextFilter(fieldName, value)}
              onSetBoolean={(fieldName, value) => {
                if (value === undefined) {
                  removeFiltersByField(fieldName);
                } else {
                  removeFiltersByField(fieldName);
                  toggleFilterValue(fieldName, String(value));
                }
              }}
              onRemoveFieldFilters={removeFiltersByField}
              onClearAll={clearFilters}
              isFacetLoading={isFacetsLoading}
              showHeader
              title="Feature Filters"
            />
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PartCardList
            data={partsData}
            isLoading={isLoading}
            page={page}
            onPageChange={setPage}
            onPartClick={handlePartClick}
          />
        </Box>
      </Stack>

      {/* Modals */}
      <CreatePartModal
        open={createPartOpen}
        onClose={() => setCreatePartOpen(false)}
        partGroups={partGroups || []}
      />
      <PartExcelUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        partGroups={partGroups || []}
      />
      <PartGroupCreateModal
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
      />
    </Box>
  );
};

export default PartsDashboardPage;
